import { useState, useMemo, useRef, useCallback, useEffect } from "react";

import { formatUserName } from "@impero-com/common/utils/formatting";
import { t } from "@impero-com/common/utils/i18n";
import { assertNever } from "@impero-com/common/utils/obj";
import { Svg, Button, TSTTable, TST_COLUMN_SIZE } from "@impero-com/ui";
import RemoveIconSvg from "@impero-com/ui/img/remove.svg";
import {
    createColumnHelper,
    useReactTable,
    getCoreRowModel,
} from "@tanstack/react-table";
import { message } from "antd";
import { isEqual } from "lodash-es";

import {
    NotificationRecipientKind,
    UserGroupView,
    UserOverview,
} from "api/generated/model";
import { UserOrUserGroupTypeahead } from "components/userTypeahead";
import { useAllUsersSuspenseQuery } from "org/control/bulkAssign/queries/useFetchUsersQuery";
import { useUserGroupsQuery } from "org/user/queries/useUserGroupsQuery";
import { UserOrUserGroupOverview } from "utils/user";

import { GroupIcon } from "../cellWidgets";

import { NOTIFY_ON_CONTROL_COMPLETION } from "./NotifyOnControlCompletionSection";
import {
    fromNotificationRecipientKind,
    toNotificationRecipientKind,
    isRecipientAlreadyPresent,
    deduplicateRecipients,
} from "./NotifyOnControlCompletionUtils";
import * as theme from "./theme.css";
import {
    toUserOrUserGroupOverviewEnum,
    UserOrUserGroupOverviewEnum,
} from "./types";

interface NotifyOnControlCompletionTableProps {
    value: NotificationRecipientKind[];
    onNotificationRecipientChanged: (
        recipientsKind: NotificationRecipientKind
    ) => void;
    onNotificationRecipientRemoved: (
        recipientsKind: NotificationRecipientKind
    ) => void;
    onUserAdded: (user: UserOverview) => void;
    onUserGroupAdded: (userGroup: UserGroupView) => void;
    showTable: boolean;
}

const TYPEAHEAD_PLACEHOLDER: UserOrUserGroupOverviewEnumWithPlaceholder = {
    type: "TypeaheadPlaceholder",
} as const;

// Extend UserOrUserGroupOverviewEnum to include "TypeaheadPlaceholder type to allow for a typeahead input in the table"
export type UserOrUserGroupOverviewEnumWithPlaceholder =
    | UserOrUserGroupOverviewEnum
    | { type: "TypeaheadPlaceholder" };

export function NotifyOnControlCompletionTable({
    value: selectedRecipientsKind,
    onNotificationRecipientChanged,
    onNotificationRecipientRemoved,
    onUserAdded,
    onUserGroupAdded,
    showTable,
}: NotifyOnControlCompletionTableProps) {
    const { data: userData } = useAllUsersSuspenseQuery();
    const { data: userGroupData } = useUserGroupsQuery();

    const userIds = useMemo(() => userData?.map((u) => u.id) ?? [], [userData]);
    const userGroupsIds = useMemo(
        () => userGroupData?.map((g) => g.id),
        [userGroupData]
    );

    const initialRecipients: UserOrUserGroupOverviewEnum[] = useMemo(() => {
        return selectedRecipientsKind
            .map((recipient) =>
                fromNotificationRecipientKind(
                    recipient,
                    userData,
                    userGroupData ?? []
                )
            )
            .filter((r): r is UserOrUserGroupOverviewEnum => r !== null);
    }, [selectedRecipientsKind, userData, userGroupData]);

    const [selectedRecipients, setSelectedRecipients] =
        useState<UserOrUserGroupOverviewEnum[]>(initialRecipients);

    const prevInitialRecipients =
        useRef<UserOrUserGroupOverviewEnum[]>(initialRecipients);

    useEffect(() => {
        // Only update if initialRecipients actually changed (not just a re-render)
        if (!isEqual(prevInitialRecipients.current, initialRecipients)) {
            setSelectedRecipients(initialRecipients);
            prevInitialRecipients.current = initialRecipients;
        }
    }, [initialRecipients]);

    const handleAddRecipient = useCallback(
        (rawRecipient: UserOrUserGroupOverview) => {
            const recipient = toUserOrUserGroupOverviewEnum(rawRecipient);

            if (isRecipientAlreadyPresent(selectedRecipients, recipient)) {
                message.error(t("Control_NotifyMessage_RecipientAlreadyAdded"));
                return;
            }
            if (
                recipient.type === "User" &&
                !userIds.includes(recipient.user.id)
            ) {
                onUserAdded(recipient.user);
            } else if (
                recipient.type === "UserGroup" &&
                !userGroupsIds?.includes(recipient.userGroup.id)
            ) {
                onUserGroupAdded(recipient.userGroup);
            }

            setSelectedRecipients((prev) => [...prev, recipient]);
            const value = toNotificationRecipientKind(recipient);
            if (value) {
                onNotificationRecipientChanged(value);
            }
        },
        [
            selectedRecipients,
            onNotificationRecipientChanged,
            userIds,
            userGroupsIds,
            onUserAdded,
            onUserGroupAdded,
        ]
    );
    function handleRemoveRecipient(removeIndex: number) {
        setSelectedRecipients((prev) => {
            if (!prev) {
                return prev;
            }
            const toRemove = prev[removeIndex];
            const updatedRecipients = prev.filter(
                (_, idx) => idx !== removeIndex
            );
            if (toRemove) {
                const removedKind = toNotificationRecipientKind(toRemove);
                if (!removedKind) {
                    return updatedRecipients;
                }
                onNotificationRecipientRemoved(removedKind);
            }

            return updatedRecipients;
        });
    }

    //Table columns
    const columnHelper =
        createColumnHelper<UserOrUserGroupOverviewEnumWithPlaceholder>();

    const columnUser = columnHelper.accessor((row) => row, {
        header: t("Control_Notify_TableHeader"),
        cell: (info) => {
            const userOrGroup: UserOrUserGroupOverviewEnumWithPlaceholder =
                info.getValue();

            switch (userOrGroup.type) {
                case "User":
                    return (
                        <div>
                            <div className={theme.title}>
                                {formatUserName(userOrGroup.user)}
                            </div>
                            <span className={theme.description}>
                                {userOrGroup.user.email}
                            </span>
                        </div>
                    );
                case "UserGroup":
                    return (
                        <div className={theme.userGroupContainer}>
                            <div>
                                <div className={theme.title}>
                                    {userOrGroup.userGroup.name}
                                </div>
                                <div className={theme.description}>
                                    {t(
                                        "Control_Assignment_NotifyGroupDescription"
                                    )}
                                </div>
                            </div>

                            <GroupIcon />
                        </div>
                    );
                case "TypeaheadPlaceholder":
                    return (
                        <UserOrUserGroupTypeahead
                            value={undefined}
                            onSelect={handleAddRecipient}
                            id={"userOrGroup"}
                            className={theme.reviewerTypeahead}
                            placeholder={t(
                                "Control_Notify_TypeaheadPlaceholder"
                            )}
                            getPopupContainer={() =>
                                document.getElementById(
                                    NOTIFY_ON_CONTROL_COMPLETION
                                ) as HTMLDivElement
                            }
                            isAutoFocused={true}
                        />
                    );

                default:
                    return assertNever(userOrGroup);
            }
        },
    });
    const columnRemove = columnHelper.display({
        id: "remove",
        header: "",
        cell: (info) => {
            // Do not show remove button for the typeahead row
            const userOrGroup = info.row.original;
            if (userOrGroup.type === "TypeaheadPlaceholder") {
                return null;
            }
            return (
                <Button
                    variant="text"
                    size={"icon"}
                    icon={
                        <Svg src={RemoveIconSvg} className={theme.removeIcon} />
                    }
                    onClick={() => handleRemoveRecipient(info.row.index)}
                    className={theme.notifyRemoveButton}
                />
            );
        },
        size: TST_COLUMN_SIZE.CHECKBOX,
        meta: { fixedWidth: true },
    });

    const tableData = useMemo(() => {
        const uniqueRecipients = deduplicateRecipients(selectedRecipients);
        return [...uniqueRecipients, TYPEAHEAD_PLACEHOLDER];
    }, [selectedRecipients]);

    const table = useReactTable({
        data: tableData,
        columns: [columnUser, columnRemove],
        getCoreRowModel: getCoreRowModel(),
        enableRowSelection: false,
        enableSorting: false,
    });
    const tableRef = useRef<HTMLTableElement>(null);

    if (!showTable) {
        return null;
    }

    return (
        <TSTTable
            tableRef={tableRef}
            table={table}
            noResultsFallback={t("NoResults")}
            noSourceRowsFallback={t("NoResults")}
        />
    );
}
